import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Alert, Badge, Button, EmptyState, Loading, PageHeader, Panel, PanelBody, Segmented } from '../ui/index.js';
import { fetchJson } from '../api.js';

/**
 * S08 — Prix & TVA: the operator TVA station (client rules 2026-10-03).
 * Tab 1: divergent rates (vat_review) — pick the correct rate, one click.
 * Tab 2: products with NO rate from any source — fill manually.
 * Rates are the Tunisian scale only: 0 / 7 / 10 / 13 / 19.
 */

interface VatReviewRow {
  readonly id: number;
  readonly productId: number;
  readonly rates: readonly { readonly rate: number; readonly count: number; readonly sources: readonly string[] }[];
  readonly chosenRate: number | null;
  readonly product: {
    readonly sku: string;
    readonly name: string;
    readonly brand: string | null;
    readonly currentRate: number | null;
    readonly currentSource: string | null;
  };
}

interface MissingRow {
  readonly id: number;
  readonly sku: string;
  readonly name: string;
  readonly brand: string | null;
  readonly purchasePriceHt: string | number | null;
  readonly purchaseSourceKey: string | null;
}

const RATES = [0, 7, 10, 13, 19] as const;

type Tab = 'arbitrage' | 'manquantes';

function money(value: string | number | null): string {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(3) : '—';
}

export function PrixTvaPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('arbitrage');
  const [message, setMessage] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [arbitrageSearch, setArbitrageSearch] = useState('');

  const reviewQuery = useQuery({
    queryKey: ['vat-review', 'open'],
    queryFn: () => fetchJson<readonly VatReviewRow[]>('/api/v1/vat/review?status=open'),
    enabled: tab === 'arbitrage',
  });
  const missingQuery = useQuery({
    queryKey: ['vat-missing', page, query],
    queryFn: () =>
      fetchJson<{ total: number; items: readonly MissingRow[] }>(
        `/api/v1/vat/missing?page=${page}&q=${encodeURIComponent(query)}`,
      ),
    enabled: tab === 'manquantes',
  });

  const setVat = useMutation({
    mutationFn: (input: { productId: number; rate: number }) =>
      fetchJson(`/api/v1/products/${input.productId}/vat`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ rate: input.rate }),
      }),
    onSuccess: (_data, input) => {
      setMessage(`TVA fixée à ${input.rate} % (provenance : opérateur, tracée dans le journal).`);
      void queryClient.invalidateQueries({ queryKey: ['vat-review'] });
      void queryClient.invalidateQueries({ queryKey: ['vat-missing'] });
      void queryClient.invalidateQueries({ queryKey: ['kpis'] });
    },
    onError: (err) => setMessage('Échec : ' + String(err.message)),
  });

  const bulkApplyMajority = useMutation({
    mutationFn: () =>
      fetchJson<{ ok: boolean; resolvedCount: number }>('/api/v1/vat/review/bulk-apply-majority', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
      }),
    onSuccess: (data) => {
      setMessage(`✓ ${data.resolvedCount} divergence(s) TVA résolue(s) au taux majoritaire.`);
      void queryClient.invalidateQueries({ queryKey: ['vat-review'] });
      void queryClient.invalidateQueries({ queryKey: ['vat-missing'] });
      void queryClient.invalidateQueries({ queryKey: ['kpis'] });
    },
    onError: (err) => setMessage('Échec : ' + String(err.message)),
  });

  const total = missingQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 50));

  return (
    <div>
      <PageHeader
        index="S08"
        title="Prix & TVA"
        purpose="Station TVA opérateur : quand les fichiers fournisseurs se contredisent, vous choisissez le taux ; quand aucune source n'en porte, vous le saisissez. Le moteur applique provisoirement le taux majoritaire — votre décision fait foi et est journalisée."
        meta={[
          { label: 'Vue', value: tab === 'arbitrage' ? 'à arbitrer' : 'TVA manquante' },
          {
            label: 'À arbitrer',
            value: String(reviewQuery.data?.length ?? '—'),
          },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Segmented
              ariaLabel="Vue TVA"
              value={tab}
              onChange={(next) => {
                setTab(next);
                setPage(1);
              }}
              options={[
                { value: 'arbitrage', label: 'À arbitrer' },
                { value: 'manquantes', label: 'TVA manquante' },
              ]}
            />
          </div>
        }
      />

      {message !== null && (
        <Alert
          tone={message.startsWith('Échec') ? 'alarm' : 'good'}
          title="TVA"
          className="mb-4"
          actions={
            <button
              type="button"
              onClick={() => setMessage(null)}
              className="text-xs font-semibold text-ink-soft hover:text-ink"
            >
              Fermer
            </button>
          }
        >
          {message}
        </Alert>
      )}

      {tab === 'arbitrage' && (
        <>
          {(() => {
            const allReviews = reviewQuery.data ?? [];
            const filteredReviews = allReviews.filter((row) => {
              if (!arbitrageSearch.trim()) return true;
              const needle = arbitrageSearch.trim().toLowerCase();
              return (
                row.product.name.toLowerCase().includes(needle) ||
                (row.product.sku && row.product.sku.toLowerCase().includes(needle)) ||
                (row.product.brand && row.product.brand.toLowerCase().includes(needle))
              );
            });

            return (
              <>
                <Panel className="mb-4">
                  <PanelBody className="flex flex-wrap items-center gap-3">
                    <div className="relative min-w-0 flex-1 sm:max-w-xs">
                      <input
                        value={arbitrageSearch}
                        onChange={(event) => setArbitrageSearch(event.target.value)}
                        placeholder="Filtrer par nom, SKU, marque…"
                        className="h-8.5 w-full rounded-lg border border-border bg-card px-3 text-xs sm:text-sm text-ink placeholder:text-ink-ghost focus:border-herb-600 outline-none"
                      />
                    </div>
                    <span className="font-mono text-xs text-ink-faint">
                      {filteredReviews.length} / {allReviews.length} divergence(s)
                    </span>
                    <div className="ml-auto flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={bulkApplyMajority.isPending || allReviews.length === 0}
                        onClick={() => {
                          if (window.confirm(`Appliquer le taux majoritaire sur les ${allReviews.length} produits à arbitrer ?`)) {
                            bulkApplyMajority.mutate();
                          }
                        }}
                      >
                        {bulkApplyMajority.isPending ? 'Application…' : `⚡ Appliquer taux majoritaire (${allReviews.length})`}
                      </Button>
                    </div>
                  </PanelBody>
                </Panel>

                {reviewQuery.isPending && <Loading label="Lecture des divergences TVA…" />}
                {reviewQuery.isError && (
                  <Alert tone="alarm" title="Liste indisponible" className="mb-3">
                    {String(reviewQuery.error.message)}
                  </Alert>
                )}
                {reviewQuery.data !== undefined && filteredReviews.length === 0 && (
                  <EmptyState
                    title={arbitrageSearch.trim() ? "Aucun produit trouvé" : "Aucune divergence TVA"}
                    hint={arbitrageSearch.trim() ? "Aucun produit ne correspond à ce filtre." : "Les fichiers fournisseurs sont concordants sur tous les produits pricing. Les divergences apparaissent ici après un « Recalculer les prix d'achat » (S07)."}
                  />
                )}
                <div className="space-y-2">
                  {filteredReviews.map((row) => (
              <div
                key={row.id}
                className="shadow-xs flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-clay-600/30 bg-card p-3.5"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-ink">{row.product.name}</p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-2 font-mono text-xs text-ink-soft">
                    <span>{row.product.sku ?? '∅'}</span>
                    <span className="text-ink-faint">·</span>
                    <span>{row.product.brand ?? '—'}</span>
                    <span className="text-ink-faint">·</span>
                    <span>appliqué : {row.chosenRate ?? '—'} %</span>
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {row.rates.map((detail) => (
                    <span
                      key={detail.rate}
                      title={detail.sources.join(', ')}
                      className="inline-flex items-center gap-1 rounded border border-border bg-cream/60 px-2 py-0.5 font-mono text-xs text-ink"
                    >
                      {detail.rate} % ×{detail.count}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-1">
                  {RATES.map((rate) => (
                    <Button
                      key={rate}
                      type="button"
                      size="xs"
                      variant={row.chosenRate === rate ? 'primary' : 'outline'}
                      disabled={setVat.isPending}
                      onClick={() => setVat.mutate({ productId: row.productId, rate })}
                    >
                      {rate}
                    </Button>
                  ))}
                </div>
              </div>
            ))}
                </div>
              </>
            );
          })()}
        </>
      )}

      {tab === 'manquantes' && (
        <>
          <Panel className="mb-4">
            <PanelBody>
              <form
                className="flex items-center gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  setPage(1);
                  setQuery(search.trim());
                }}
              >
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="SKU, nom, marque…"
                  className="h-8.5 w-64 rounded-lg border border-border bg-card px-3 text-xs sm:text-sm text-ink placeholder:text-ink-ghost focus:border-herb-600 outline-none"
                />
                <Button type="submit" variant="outline" size="sm">
                  Chercher
                </Button>
                <span className="ml-auto font-mono text-xs text-ink-faint">
                  {total.toLocaleString('fr-FR')} produits sans TVA source
                </span>
              </form>
            </PanelBody>
          </Panel>

          {missingQuery.isPending && <Loading label="Lecture des TVA manquantes…" />}
          {missingQuery.isError && (
            <Alert tone="alarm" title="Liste indisponible" className="mb-3">
              {String(missingQuery.error.message)}
            </Alert>
          )}
          {missingQuery.data !== undefined && missingQuery.data.items.length === 0 && (
            <EmptyState
              title="Aucune TVA manquante"
              hint="Tous les SKUs vendables portent un taux (fichier fournisseur ou décision opérateur)."
            />
          )}
          <div className="space-y-1.5">
            {missingQuery.data?.items.map((item) => (
              <div
                key={item.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-border bg-card px-3 py-2 text-xs"
              >
                <span className="font-mono font-bold">{item.sku ?? '∅'}</span>
                <span className="min-w-0 flex-1 truncate text-ink">{item.name}</span>
                <span className="text-ink-faint">{item.brand ?? '—'}</span>
                {item.purchasePriceHt !== null && (
                  <span className="font-mono text-ink-soft">achat {money(item.purchasePriceHt)} HT</span>
                )}
                {item.purchaseSourceKey !== null && (
                  <Badge tone="muted">{item.purchaseSourceKey}</Badge>
                )}
                <div className="flex items-center gap-1">
                  {RATES.map((rate) => (
                    <Button
                      key={rate}
                      type="button"
                      size="xs"
                      variant="outline"
                      disabled={setVat.isPending}
                      onClick={() => setVat.mutate({ productId: item.id, rate })}
                    >
                      {rate}
                    </Button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="mt-3 flex items-center justify-between">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ← Précédente
              </Button>
              <span className="font-mono text-xs text-ink-faint">
                Page {page} / {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Suivante →
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
